import { useEffect, useRef, type RefObject } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import {
  Flex,
  Typography,
  Button,
  Form,
  Input,
  DatePicker,
  notification,
  Spin,
} from "antd";
import { RcFile } from "antd/es/upload/interface";
import dayjs from "dayjs";
import { useForm, Controller } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { journalsQueries, useJournalQuery, JournalRecordPageShell } from "@/entities/journal/index.ts";
import { SignersScroll, dedupeSignersByUserId, formatSectionTwoSignerLabel, tagRepresentativesWithSource } from "@/features/signers-infinite-scroll/index.ts";
import {
  useCoordinator,
  useFileLink,
  useFileMetadata,
} from "@shared/lib/hooks";
import { useAppRoutes } from "@shared/lib/react-router";
import { useUnifiedAutoResize, NOTIFICATION_MESSAGES } from "@shared/utils";
import { AddRecordSchema } from "../../model/add-record.schema";
import { useCreateSectionTwoRecord } from "../../model/use-create-section-two-record.ts";
import { useUpdateSectionTwoRecordSigned } from "../../model/use-update-section-two-record-signed.ts";
import { useUpdateSectionTwoRecord } from "../../model/use-update-section-two-record.ts";
import { FormFileSelector } from "../form-file-selector";
import { OrganizationFields } from "../organization-fields/organization-fields.tsx";
import "./journal-section-form-page.scss";

export function JournalSectionFormPage() {
  const navigate = useNavigate();
  const { itemNoVersionId: itemNoVersionIdParam } = useParams<{ itemNoVersionId?: string }>();
  const { titleId, mainJournalId, projectId, objectId } = useCoordinator();
  const appRoutes = useAppRoutes();

  const isEditMode = itemNoVersionIdParam && itemNoVersionIdParam !== "new";
  const itemNoVersionId = isEditMode ? itemNoVersionIdParam : undefined;

  const { data: dataTitle } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const { data: mainJournal } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const sectionTwoId = mainJournal?.mainJournal?.section2?.sectionId || "";
  const sectionTwoListId = mainJournal?.mainJournal?.section2?.sectionListId || "";

  const {
    data: recordToEdit,
    isLoading: isLoadingRecord,
    isLoadingError,
  } = useJournalQuery(
    "get",
    "/api/v1/journals/section-two/items/{itemNoVersionId}",
    {
      params: {
        path: {
          itemNoVersionId: itemNoVersionId!,
        },
      },
    },
    {
      enabled: !!itemNoVersionId,
    },
  );

  const AddRecordWithReasonSchema = AddRecordSchema.extend({
    actionDetails: z.string().optional(),
  });

  type AddRecordWithReasonFormType = z.infer<typeof AddRecordWithReasonSchema>;

  const { handleSubmit, control, reset, setValue, trigger } = useForm<AddRecordWithReasonFormType>({
    resolver: zodResolver(AddRecordWithReasonSchema),
    defaultValues: {
      journalKeepersList: [
        {
          company: {
            companyId: null as any,
            projectParticipantId: null as any,
          },
          authorizedRepresentativesList: [],
        },
      ],
    },
  });

  useEffect(() => {
    if (!isLoadingRecord && !isLoadingError && recordToEdit) {
      reset({
        signer: recordToEdit.signer?.userId!,
        dateHandover: recordToEdit.dateHandover
          ? dayjs(recordToEdit.dateHandover)
          : null,
        specialJournalDate: dayjs(recordToEdit.specialJournalDate),
        specialJournalName: recordToEdit.specialJournalName,
        journalKeepersList: recordToEdit.journalKeepersList?.map((keeper) => ({
          company: keeper.company
            ? {
                companyId: keeper.company?.id,
                projectParticipantId: keeper.company?.projectParticipantId,
                companyName: keeper.company?.fullName,
              }
            : keeper.company,
          authorizedRepresentativesList:
            keeper.authorizedRepresentativesList?.map((representative) => ({
              user: {
                surname: representative.surname,
                firstName: representative.firstName,
                organization: keeper.company?.fullName,
                position: representative.position,
                userId: representative.userId,
                userCompanyHistoryId: representative.userCompanyHistoryId,
              },
            })),
        })),
        file: {
          id: recordToEdit.journalScanId,
        },
      });
    }
  }, [isLoadingRecord, isLoadingError, recordToEdit, reset]);

  const { data: fileMeta } = useFileMetadata({
    fileId: recordToEdit?.journalScanId,
  });

  const { data: fileLink } = useFileLink({
    fileId: recordToEdit?.journalScanId,
  });

  const { mutate: addRecord, isPending: isCreatePending } =
    useCreateSectionTwoRecord({ sectionTwoId });

  const { mutate: updateRecord, isPending: isUpdatePending } =
    useUpdateSectionTwoRecord({
      sectionTwoId,
      itemNoVersionId,
    });

  const { mutate: updateRecordSigned, isPending: isUpdateSignedPending } =
    useUpdateSectionTwoRecordSigned({
      sectionTwoId,
      sectionTwoListId,
      versionId: recordToEdit?.currentVersionId!,
      itemNoVersionId: itemNoVersionId!,
    });

  const isSaving = isCreatePending || isUpdatePending || isUpdateSignedPending;
  const isFormLoading = Boolean(isEditMode && isLoadingRecord && !recordToEdit);
  const showPageLoader = isFormLoading || isSaving;
  const loaderTip = isSaving ? "Сохранение..." : "Загрузка...";

  const navigateToSectionList = () => {
    navigate(
      appRoutes.journals.section2(projectId, objectId, mainJournalId, titleId),
    );
  };

  const handleClose = () => {
    if (showPageLoader) return;
    navigateToSectionList();
  };

  const handleAddRecord = (data: AddRecordWithReasonFormType) => {
    if (!itemNoVersionId) {
      addRecord(
        {
          mainJournalId,
          section2Id: sectionTwoId,
          section2ListId: sectionTwoListId,
          specialJournalName: data.specialJournalName,
          specialJournalDate: data.specialJournalDate.format("YYYY-MM-DD"),
          journalKeepersList: data.journalKeepersList,
          dateHandover: data.dateHandover?.format("YYYY-MM-DD"),
          signer: data.signer,
          file: data.file,
        },
        {
          onSuccess: () => {
            reset();
            navigateToSectionList();
          },
          onError: (error: any) => {
            if (error?.response?.data?.code === 409 &&
                error?.response?.data?.error === "conflict_request") {
              notification.error({
                message: NOTIFICATION_MESSAGES.SAVE_DUPLICATE_ORG_ERROR, // "Ошибка сохранения"
                description: NOTIFICATION_MESSAGES.SAVE_DUPLICATE_ORG_ERROR_DESC, // "Нельзя добавить две одинаковые организации в запись"
                placement: "topRight",
              });
            }
          },
        },
      );
    } else if (recordToEdit?.status === "SIGNED") {
      updateRecordSigned(
        {
          mainJournalId,
          section2Id: sectionTwoId,
          section2ListId: sectionTwoListId,
          specialJournalName: data.specialJournalName,
          specialJournalDate: data.specialJournalDate.format("YYYY-MM-DD"),
          journalKeepersList: data.journalKeepersList,
          dateHandover: data.dateHandover?.format("YYYY-MM-DD"),
          signer: data.signer,
          file: data.file,
          actionDetails: data.actionDetails || "",
        },
        {
          onSuccess: () => {
            reset();
            navigateToSectionList();
          },
          onError: (error: any) => {
            if (error?.response?.data?.code === 409 &&
                error?.response?.data?.error === "conflict_request") {
              notification.error({
                message: NOTIFICATION_MESSAGES.SAVE_DUPLICATE_ORG_ERROR, // "Ошибка сохранения"
                description: NOTIFICATION_MESSAGES.SAVE_DUPLICATE_ORG_ERROR_DESC, // "Нельзя добавить две одинаковые организации в запись"
                placement: "topRight",
              });
            }
          },
        },
      );
    } else {
      const isFileDeleted = !!recordToEdit?.journalScanId && !data.file;
      updateRecord(
        {
          mainJournalId,
          section2Id: sectionTwoId,
          section2ListId: sectionTwoListId,
          specialJournalName: data.specialJournalName,
          specialJournalDate: data.specialJournalDate.format("YYYY-MM-DD"),
          journalKeepersList: data.journalKeepersList,
          dateHandover: data.dateHandover?.format("YYYY-MM-DD"),
          signer: data.signer,
          file: isFileDeleted ? null : data.file,
        },
        {
          onSuccess: () => {
            reset();
            navigateToSectionList();
          },
          onError: (error: any) => {
            if (error?.response?.data?.code === 409 &&
                error?.response?.data?.error === "conflict_request") {
              notification.error({
                message: NOTIFICATION_MESSAGES.SAVE_DUPLICATE_ORG_ERROR, // "Ошибка сохранения"
                description: NOTIFICATION_MESSAGES.SAVE_DUPLICATE_ORG_ERROR_DESC, // "Нельзя добавить две одинаковые организации в запись"
                placement: "topRight",
              });
            }
          },
        },
      );
    }
  };

  const handleCancel = () => {
    if (showPageLoader) return;
    reset();
    handleClose();
  };

  const handleSignerSelect = (signer: any) => {
    setValue("signer", signer.user!.userId!);
    trigger("signer");
  };

  const onFileSelect = (file: RcFile) => {
    setValue(`file`, {
      file,
      id: file.uid,
    });
  };

  const onFileRemove = () => {
    setValue("file", undefined);
  };

  const taggedRepresentatives = tagRepresentativesWithSource(
    dataTitle?.mainJournal?.titleJournal?.developer?.representatives,
    dataTitle?.mainJournal?.titleJournal?.technicalCustomer?.representatives,
  );

  const uniqueRepresentatives = dedupeSignersByUserId(taggedRepresentatives);

  const editSignerLabel = recordToEdit?.signer
    ? formatSectionTwoSignerLabel(
        taggedRepresentatives.find(
          (rep) => rep.user?.userId === recordToEdit.signer?.userId,
        ) ?? recordToEdit.signer,
      )
    : undefined;

  const wrapperRef = useRef<HTMLDivElement>(null);
  useUnifiedAutoResize(wrapperRef as RefObject<HTMLElement>);

  return (
    <JournalRecordPageShell ref={wrapperRef} onClose={handleClose}>
      <Spin
        spinning={showPageLoader}
        size="large"
        tip={loaderTip}
        className="journal-section-form-page-spin"
      >
      <Flex vertical align="center" gap="24px" className="journal-section-form-page-shell">
        <Flex
          className="journal-section-form-page-title-block"
          justify="space-between"
          align="center"
        >
          <Typography.Title level={3} style={{ margin: 0 }}>
            {itemNoVersionId
              ? "Редактирование записи"
              : "Добавление записи в раздел 2 ОЖР"}
          </Typography.Title>
        </Flex>

        {recordToEdit?.status === "SIGNED" && (
          <div className="edit-notice">
            В случае внесения изменений в записи общего журнала в электронной
            форме создается новая версия записи. Запись нужно будет подписать
            заново. Понадобится заполнить поле о причинах изменений.
          </div>
        )}

        <Form
          layout="vertical"
          className="journal-section-form-page-form"
          style={{ width: "100%", maxWidth: 720 }}
        >
          <FormItem
            control={control}
            name="specialJournalName"
            label="Наименование специального журнала"
            style={{ margin: 0 }}
          >
            <Input width="100%" maxLength={255} showCount />
          </FormItem>
          <FormItem
            control={control}
            name="specialJournalDate"
            label="Дата создания журнала"
            style={{ margin: 0 }}
          >
            <DatePicker
              format="DD.MM.YYYY"
              disabledDate={(current) => current && current.isAfter(dayjs())}
            />
          </FormItem>
          <FormItem
            control={control}
            name="journalKeepersList"
            style={{ margin: 0 }}
          >
            <OrganizationFields setValue={setValue} control={control} trigger={trigger} />
          </FormItem>
          <FormItem
            control={control}
            name="dateHandover"
            label="Дата передачи журнала застройщику или техническому заказчику"
            style={{ margin: 0 }}
          >
            <DatePicker format="DD.MM.YYYY" />
          </FormItem>
          <Controller
            control={control}
            name="file"
            render={() => (
              <FormFileSelector
                defaultFileLink={fileLink?.content}
                defaultFileName={fileMeta?.[0]?.fileName}
                defaultFileId={recordToEdit?.journalScanId}
                onFileSelect={onFileSelect}
                onFileRemove={onFileRemove}
              />
            )}
          />
          <Controller
            control={control}
            name="signer"
            render={({ fieldState }) => (
              <Form.Item
                validateStatus={fieldState.error ? "error" : ""}
                help={fieldState.error?.message}
                style={{ margin: 0 }}
                label="Уполномоченный представитель застройщика или технического заказчика, лица, ответственного за эксплуатацию здания, сооружения, и (или) регионального оператора"
              >
                <SignersScroll
                  users={uniqueRepresentatives}
                  defaultValue={editSignerLabel}
                  style={{ width: "100%" }}
                  titleId={titleId}
                  onSignerSelect={handleSignerSelect}
                />
                <span className="journal-form-hint">
                  Этому человеку будет отправлена данная запись на подписание
                </span>
              </Form.Item>
            )}
          />
          {recordToEdit?.status === "SIGNED" && (
            <FormItem
              control={control}
              name="actionDetails"
              label="Причина изменений"
              style={{ margin: "16px 0" }}
            >
              <Input.TextArea
                rows={3}
                placeholder="Опишите причину изменений..."
                maxLength={255}
                showCount
              />
            </FormItem>
          )}
        </Form>

        <Flex
          className="journal-section-form-page-actions"
          style={{ width: "100%", maxWidth: 720, marginBottom: 24 }}
          gap="16px"
        >
          <Button
            type="primary"
            onClick={handleSubmit(handleAddRecord)}
            disabled={showPageLoader}
            style={{ height: 44 }}
          >
            {itemNoVersionId ? "Обновить запись" : "Добавить запись"}
          </Button>
          <Button
            type="default"
            onClick={handleCancel}
            disabled={showPageLoader}
            style={{ height: 44 }}
          >
            Отмена
          </Button>
        </Flex>
      </Flex>
      </Spin>
    </JournalRecordPageShell>
  );
}
